import React, { useEffect, useState } from "react";
import "../Table/Table.css";
import axios from "axios";
import toast, { Toaster } from 'react-hot-toast';
import Loading from "../Loading";
import LocationDetail from "./LocationDetail";
import ServiceDetail from "./ServiceDetail";
import { FaEye, FaEdit, FaDownload } from "react-icons/fa";
import { Tabs, Tab, Box, Button, Modal, Typography, Input, TextField, Tooltip} from "@mui/material";
import { useVendor } from "../../utils/Context";
import { useNavigate } from "react-router-dom";
import { getPermissionByPrefix } from "../../utils/permissions";
import DataTableLocation from "../DataTable/DataTableLocation";
import { Pagination, Select, MenuItem } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { useErrorToast } from "../../hooks/useErrorToast";
import { useGridApiRef, gridFilteredSortedRowIdsSelector } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PiFileCsv } from "react-icons/pi";

function LocationTable() {
  const showError = useErrorToast();
  const navigate = useNavigate();
    const queryClient = useQueryClient();

  const [contractNumber, setContractNumber] = useState("");
  const [invoiceData, setInvoiceData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [showServiceDetail, setShowServiceDetail] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [serviceExpandedRows, setServiceExpandedRows] = useState({});
  const [loadingFull, setLoadingFull] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(200);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [locationViewType, setLocationViewType] = useState('grouped');
  const apiRef = useGridApiRef();

  const { selectedVendor } = useVendor();

  const { is_view, is_edit } = getPermissionByPrefix("manage_location");


 const fetchLocations = async ({ queryKey }) => {
    const [_key, page, limit, vendorId] = queryKey;
    const token = localStorage.getItem("tokenInv");

    let url = `${process.env.REACT_APP_URL}/get-locations?page=${page}&limit=${limit}&is_discrepancy=true`;
    if (vendorId !== -1) {
      url += `&vendor_id=${vendorId}`;
    }

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data; // Expect { data: [...], total: number }
  };

  const {
    data: locationsData,
    isLoading: contractLoading,
    isError: isErrorLocations,
    error: errorLocations,
    refetch: refetchLocations,
  } = useQuery({
    queryKey: ["locations", page, limit, selectedVendor],
    queryFn: fetchLocations,
    keepPreviousData: true, // keep old data while fetching new page
    staleTime: 5 * 60 * 1000, // 5 minutes caching
    retry: false,
    onError: (error) => {
      if (error.response?.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        localStorage.removeItem("roleInv");
        navigate("/");
      } else {
        showError(error?.response?.data?.message || "Something went wrong while fetching locations");
      }
    },
  });

  const contracts = locationsData?.data || [];
  const totalCount = locationsData?.total || 0;

  const fetchInvoiceAndSummary = async (contract_no) => {
    setInvoiceLoading(true);
    setLoadingFull(true);
    try {
      const token = localStorage.getItem("tokenInv");
      const invoiceResponse = await axios.get(
        `${process.env.REACT_APP_URL}/get-invoice-details-by-location/${contract_no}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        }
      );
      setInvoiceData(invoiceResponse?.data);
      setShowDetails(true);
    } catch (error) {
      if (error.response.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        localStorage.removeItem("roleInv");
        navigate("/");
      } else {
        showError(error?.response?.data?.message || "Something went wrong. Please try again.");
        // toast.error(
        //   error?.response?.data?.message ||
        //     "Something went wrong. Please try again."
        // );
      }
      setShowDetails(false);
    } finally {
      setInvoiceLoading(false);
      setLoadingFull(false);
    }
  };

  const fetchServices = async (contract_no) => {
    setInvoiceLoading(true);
    setLoadingFull(true);
    try {
      const token = localStorage.getItem("tokenInv");
      const invoiceResponse = await axios.get(
        `${process.env.REACT_APP_URL}/get-services-by-location/${contract_no}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        }
      );
      setServiceData(invoiceResponse?.data);
      setShowServiceDetail(true);
    } catch (error) {
      if (error.response.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        localStorage.removeItem("roleInv");
        navigate("/");
      } else {
        showError(error?.response?.data?.message || "Something went wrong. Please try again.");
        // toast.error(
        //   error?.response?.data?.message ||
        //     "Something went wrong. Please try again."
        // );
      }
      setShowServiceDetail(false);
      console.error("Failed to fetch services of location", error);
    } finally {
      setInvoiceLoading(false);
      setLoadingFull(false);
    }
  };

  const handleViewButtonClick = (contract) => {
    setShowServiceDetail(false);
    setContractNumber(contract.location_id);
    setServiceExpandedRows({});

    setExpandedRows((prevState) => {
      const newExpandedState = {
        [contract.location_id]: !prevState[contract.location_id],
      };
      return newExpandedState;
    });

    if (!expandedRows[contract.location_id]) {
      fetchInvoiceAndSummary(contract.location_id);
    } else {
      setShowDetails(false);
    }

    setTimeout(() => {
      const scrollToBottomElement = document.getElementById("scroll-to-bottom");
      if (scrollToBottomElement) {
        scrollToBottomElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
  };

  const handleViewButtonServices = (contract) => {
    setShowDetails(false);
    setContractNumber(contract.location_id);
    setExpandedRows({});

    setServiceExpandedRows((prevState) => {
      const newExpandedState = {
        [contract.location_id]: !prevState[contract.location_id],
      };
      return newExpandedState;
    });

    if (!serviceExpandedRows[contract.location_id]) {
      fetchServices(contract.location_id);
    } else {
      setShowServiceDetail(false);
    }

    setTimeout(() => {
      const scrollToBottomElement = document.getElementById("scroll-to-bottom");
      if (scrollToBottomElement) {
        scrollToBottomElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
  };

  const handleEditClick = (contract) => {
    setSelectedLocation(contract);
    setLocationName(contract.country);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseConfirm = () => {
    setConfirmationOpen(false);
  };

  const handleSubmit = () => {
    setConfirmationOpen(true);
  };

  const confirmUpdate = async () => {
    if (!locationName) {
      toast.error("Location Name cannot be empty");
      return;
    }
    try {
      const token = localStorage.getItem("tokenInv");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/update-location/${selectedLocation.id}`,
        {
          updated_country: locationName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Location updated successfully");
      setOpen(false);
      setLocationName("");
      setSelectedLocation("");
      // handleLocation();
            queryClient.invalidateQueries(['locations']);

    } catch (error) {
      if (error.response.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        localStorage.removeItem("roleInv");
        navigate("/");
      } else {
        toast.error("Failed to update location");
      }
    } finally {
      setConfirmationOpen(false);
    }
  };

  const cancelUpdate = () => {
    setConfirmationOpen(false);
  };

  let columns = [
    { field: "location_id", headerName: "Location ID", width: 130 },
    { field: "loc_name", headerName: "Location Name", width: 300 },
    { field: "city", headerName: "City", width: 150 },
    { field: "country", headerName: "Country", width: 150 },
    { field: "currencies", headerName: "Currency", width: 120 },
    { field: "total_amount_usd", headerName: "Total Amount USD", width: 170 },
    { field: "total_converted_usd", headerName: "NET Spend USD", width: 180 },
    { field: "discrepancy", headerName: "Discrepancy", width: 150 },
    { field: "total_adj", headerName: "Total Adjustment", width: 150 },
    { field: "final_discrepancy", headerName: "Final Discrepancy", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 400,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleViewButtonClick(params.row)}
            sx={{
              fontSize: "12px",
              marginLeft: "5px",
              display: "flex",
              margin: "auto",
              marginTop: "7px",
              backgroundColor: "rgb(124, 46, 160)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaEye color="#FFF" size={20} />
              View Invoice
            </div>
          </Button>
          <Button
            variant="contained"
            onClick={() => handleViewButtonServices(params.row)}
            sx={{
              fontSize: "12px",
              marginLeft: "5px",
              display: "flex",
              marginTop: "7px",
              backgroundColor: "rgb(124, 46, 160)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaEye color="#FFF" size={20} />
              View Services
            </div>
          </Button>
 {is_edit && 
          <Button
            variant="contained"
            onClick={() => handleEditClick(params.row)}
            sx={{
              fontSize: "12px",
              marginLeft: "5px",
              display: "flex",
              marginTop: "7px",
              backgroundColor: "rgb(124, 46, 160)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaEdit color="#FFF" size={20} title="Edit" />
            </div>
          </Button>}
        </div>
      ),
    },
  ];

  columns = columns.map(col => {
    if (col.headerName === 'Discrepancy') {
      return { 
        ...col, 
        field: locationViewType === 'grouped' ? 'grouped_discrepancy_amount' : 'discrepancy'
      };
    }
    if (col.headerName === 'Final Discrepancy') {
      return { 
        ...col, 
        field: locationViewType === 'grouped' ? 'grouped_discrepancy_final_amount' : 'final_discrepancy'
      };
    }
    return col;
  });
  const rows = contracts?.map((contract, index) => ({
    id: contract?.id,
    location_id: contract?.location_id || "N/A",
    country: contract?.updated_country || contract?.country || "N/A",
    loc_name: contract?.loc_name || "N/A",
    city: contract?.city || "N/A",
    currencies: contract?.currencies && contract.currencies.length > 0 ? contract.currencies.join(", ") : "N/A",
    total_amount_usd: parseFloat(contract?.total_amount_usd || 0).toFixed(2),
    total_converted_usd: parseFloat(contract?.total_converted_usd || 0).toFixed(2),
    discrepancy: parseFloat(contract?.discrepancy || 0).toFixed(2),
    grouped_discrepancy_amount: parseFloat(contract?.grouped_discrepancy_amount || 0).toFixed(2),
    total_adj: parseFloat(contract?.total_adj || 0).toFixed(2),
    final_discrepancy: parseFloat(contract?.final_discrepancy || 0).toFixed(2),
    grouped_discrepancy_final_amount: parseFloat(contract?.grouped_discrepancy_final_amount || 0).toFixed(2),
  }));

   const handleDownloadCSV = () => {
    if (!rows || rows.length === 0) {
      toast.error("No data available to download");
      return;
    }

    let rowsToExport = rows;
    if (apiRef?.current?.getRow) {
      try {
        const filteredIds = gridFilteredSortedRowIdsSelector(apiRef);
        if (filteredIds) {
          rowsToExport = filteredIds.map(id => apiRef.current.getRow(id)).filter(Boolean);
        }
      } catch (e) {
        console.error("Failed to get filtered rows", e);
      }
    }

    if (!rowsToExport || rowsToExport.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const headers = [
      "Location ID",
      "Location Name",
      "City",
      "Country",
      "Currency",
      "Total Amount USD",
      "NET Spend USD",
      "Discrepancy",
      "Total Adjustment",
      "Final Discrepancy",
    ];

    const csvRows = [
      headers.join(","),
      ...rowsToExport.map((row) =>
        [
          row.location_id,
          `"${row.loc_name}"`,
          `"${row.city || ''}"`,
          `"${row.country}"`, // Wrap in quotes to handle commas
          `"${row.currencies}"`, // Wrap in quotes in case of multiple currencies separated by comma
          row.total_amount_usd,
          row.total_converted_usd,
          locationViewType === 'grouped' ? row.grouped_discrepancy_amount : row.discrepancy,
          row.total_adj,
          locationViewType === 'grouped' ? row.grouped_discrepancy_final_amount : row.final_discrepancy,
        ].join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${locationViewType === 'grouped' ? 'Grouped_Locations' : 'Individual_Locations'}_page_${page}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!is_view) {
  return <div>You do not have permission to view this page.</div>;
}

  let displayCount = totalCount;
  
  const hasActiveFilter = 
    (filterModel?.items && filterModel.items.some(item => item.value != null && item.value !== '')) || 
    (filterModel?.quickFilterValues && filterModel.quickFilterValues.length > 0);

  if (hasActiveFilter && apiRef?.current?.getRow) {
    try {
      const filteredIds = gridFilteredSortedRowIdsSelector(apiRef);
      if (filteredIds) {
        displayCount = filteredIds.length;
      }
    } catch (e) {}
  }

  return (
    <>
<Toaster position="top-right" reverseOrder={false}/>
      {loadingFull && <Loading />}
            <div className="table-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Locations</h2>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f1f5f9',
              borderRadius: '20px',
              padding: '2px',
              marginRight: '15px'
            }}
          >
            <Box
              onClick={() => setLocationViewType('grouped')}
              sx={{
                padding: '4px 12px',
                borderRadius: '18px',
                fontSize: '0.75rem',
                fontWeight: locationViewType === 'grouped' ? 600 : 400,
                color: locationViewType === 'grouped' ? '#fff' : '#64748b',
                backgroundColor: locationViewType === 'grouped' ? '#7C2EA0' : 'transparent',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
              }}
            >
              Grouped
            </Box>
            <Box
              onClick={() => setLocationViewType('individual')}
              sx={{
                padding: '4px 12px',
                borderRadius: '18px',
                fontSize: '0.75rem',
                fontWeight: locationViewType === 'individual' ? 600 : 400,
                color: locationViewType === 'individual' ? '#fff' : '#64748b',
                backgroundColor: locationViewType === 'individual' ? '#7C2EA0' : 'transparent',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
              }}
            >
              Individual
            </Box>
          </Box>
        <Tooltip title="Download CSV" placement="top" arrow>
          <span
            onClick={handleDownloadCSV}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              backgroundColor: "#f5f5f5",
              transition: "all 0.2s ease",
              margin: "10px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
          >
            <PiFileCsv size={20} color="#333" />
          </span>
        </Tooltip>
        </Box>
      </div>
      <div style={{ marginBottom: "30px" }}>
        {/* <DataTable columns={columns} rows={rows} loading={contractLoading} /> */}
      <DataTableLocation
  columns={columns}
  rows={rows}
  height={450}
  loading={contractLoading}
  apiRef={apiRef}
  onFilterModelChange={(model) => setFilterModel(model)}
/>
<TablePagination
  component="div"
  count={displayCount}
  page={page - 1} // TablePagination is 0-based
  onPageChange={(event, newPage) => setPage(newPage + 1)}
  rowsPerPage={limit}
  onRowsPerPageChange={(event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  }}
  rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
  labelRowsPerPage="Rows per page:"
  labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
/>



      </div>
      <div style={{ marginBottom: "40px" }}>
        {showDetails && invoiceData && (
          <>
            <LocationDetail
              data={invoiceData}
              contractNumber={contractNumber}
            />
          </>
        )}

        {showServiceDetail && serviceData && (
          <>
            <ServiceDetail data={serviceData} contractNumber={contractNumber} />
          </>
        )}
        <div id="scroll-to-bottom"></div>

        <Modal
          open={open}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose(e);
            }
          }}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              width: 400,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              id="modal-title"
              variant="h6"
              component="h2"
              sx={{
                fontSize: "24px",
                marginBottom: "15px",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              Edit Country
            </Typography>

            <TextField
              fullWidth
              label="Country Name"
              variant="outlined"
              required
              name="locationname"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                onClick={handleSubmit}
                style={{
                  flex: 1,
                  marginRight: "10px",
                  fontSize: "18px",
                  background: "#7C2EA0",
                  color: "white",
                  padding: "12px",
                }}
              >
                Submit
              </Button>
              <Button
                onClick={handleClose}
                style={{
                  flex: 1,
                  fontSize: "18px",
                  background: "#7C2EA0",
                  color: "white",
                  padding: "12px",
                }}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>

        <Modal
          open={confirmationOpen}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              cancelUpdate(e);
            }
          }}
          // onClose={cancelUpdate}
          aria-labelledby="confirmation-title"
          aria-describedby="confirmation-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              width: 400,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              id="confirmation-title"
              variant="h6"
              component="h2"
              sx={{
                fontSize: "24px",
                marginBottom: "15px",
                textAlign: "center",
              }}
            >
              Are you sure you want to update the location?
            </Typography>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                onClick={confirmUpdate}
                style={{
                  flex: 1,
                  marginRight: "10px",
                  fontSize: "18px",
                  background: "#7C2EA0",
                  color: "white",
                  padding: "12px",
                }}
              >
                Yes, Update
              </Button>
              <Button
                onClick={cancelUpdate}
                style={{
                  flex: 1,
                  fontSize: "18px",
                  background: "#7C2EA0",
                  color: "white",
                  padding: "12px",
                }}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

export default LocationTable;
